<template>
  <div>
    <div class="flex flex-col">
      <div
        v-if="shouldDisplayPriceDetails && !isOutOfStock"
        class="body-2-bold flex md:justify-end"
      >
        {{ i18n(translations.productRefurbishedPriceLabel) }}
      </div>
      <div class="flex gap-x-4 md:justify-end">
        <div class="heading-3" data-qa="price">
          {{ i18n.price(item.price) }}
        </div>

        <div
          v-if="shouldDisplayTaxIncluded && !isOutOfStock"
          class="flex-row md:mt-2"
        >
          <span class="body-2-bold whitespace-nowrap">
            {{ i18n(translations.vatIncluded) }}
          </span>
        </div>
      </div>

      <div v-if="!isOutOfStock && item.formerPrice">
        <div class="flex flex-row items-baseline gap-8 md:justify-end">
          <RevTag
            v-if="shouldDisplayYouSave"
            class="whitespace-nowrap"
            :label="
              i18n(translations.youSaveDisplay, {
                reduction: i18n.price(item.formerPrice - item.price),
              })
            "
            variant="success"
          />
          <div v-if="shouldDisplayFormerPrice" class="body-2 z-[2]">
            <StrikedProductPrice :price="item.formerPrice" />
          </div>
        </div>
      </div>
    </div>

    <RevTag
      v-if="item.mobilePlan && hasSubsidy(item.mobilePlan.selectedOffer)"
      class="mt-4"
      :class="{
        'opacity-50 grayscale-[100%]': isOutOfStock,
      }"
      data-qa="mobile-plan-subsidy"
      :label="
        i18n(translations.bundlingDiscount, {
          price: i18n.price(item.mobilePlan.selectedOffer.subsidy),
        })
      "
      variant="primary"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevTag } from '@ds/components/Tag'

import { hasSubsidy } from '~/scopes/checkout/utils/mobile-plan'

import StrikedProductPrice from '../StrikedProductPrice/StrikedProductPrice.vue'

import translations from './PriceBlock.translations'

const props = defineProps<{
  item: CartItem
  isOutOfStock: boolean
}>()

const i18n = useI18n()

const {
  market: { countryCode },
} = useMarketplace()

const shouldDisplayFormerPrice = computed(
  () =>
    props.item.existsNew &&
    props.item.formerPrice > props.item.price &&
    ![MarketCountryCode.JP].includes(countryCode),
)

const shouldDisplayYouSave = computed(
  () =>
    props.item.existsNew &&
    props.item.formerPrice > props.item.price &&
    ![MarketCountryCode.JP].includes(countryCode),
)

const shouldDisplayPriceDetails = computed(
  () => props.item.existsNew && [MarketCountryCode.JP].includes(countryCode),
)

const shouldDisplayTaxIncluded = computed(() =>
  [MarketCountryCode.JP].includes(countryCode),
)
</script>
